<template>
  <div v-if="!props.hidden && availableBtns.length" class="gap-x-8px flex items-center">
    <MButton
      v-for="btn in availableBtns"
      :key="btn.code"
      :color="btn.color"
      :disabled="props.disabled"
      @click="handleClick(btn)"
    >
      {{ t(btn.label!) }}
    </MButton>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import type { ActionBtnItem } from '@mts/render';
  import { useConfirm } from '@mts-fe/utils';
  import { MButton } from '@mts/components/Button';
  import { t } from '@mts/hooks/web/useI18n';
  import { createSchemaApi } from '@mts/render/api';
  import { mtsEmitter } from '@mts/utils';
  import type { ApprovalFlowProps } from './types';

  /** 审批流按钮组 */
  defineOptions({ name: 'ApprovalFlowBtn', inheritAttrs: false });

  const APPROVAL_FLOW_ACTIONS: Recordable<ActionBtnItem> = {
    canApprove: { eventType: 'BUTTON', label: 'g.26100005', color: 'success' },
    canWithdraw: {
      eventType: 'BUTTON',
      label: 'g.26090007',
      color: 'warning',
      eventApi: '/ims-system/approval/withdraw',
      openConfirm: { content: 'g.26090009' },
      flatParam: true,
      routeUrl: 'replace@',
    },
  };

  const props = withDefaults(defineProps<ApprovalFlowProps>(), {
    params: () => ({}),
  });

  const getParams = () => {
    return {
      bizType: props.bizType,
      bizId: props.record?.bizId,
      ...(typeof props.params === 'function' ? props.params() : props.params),
    };
  };

  const flags = ref<string[]>([]);
  const loadFlags = async () => {
    try {
      const params = getParams();
      if (!params.bizType || !params.bizId) return;
      const res = await createSchemaApi(props.action || '/ims-system/approval/operateFlags')?.(
        params,
      );
      if (res) {
        flags.value = res;
      }
    } catch (e) {
      console.error('[ApprovalFlowBtn] operateFlags 加载失败', e);
    }
  };

  onMounted(loadFlags);
  watch(() => props.record?.bizId, loadFlags);

  const availableBtns = computed(() =>
    flags.value
      .filter((code) => APPROVAL_FLOW_ACTIONS[code])
      .map((code) => ({ code, ...APPROVAL_FLOW_ACTIONS[code] })),
  );

  const handleClick = (actionEvent: ActionBtnItem) => {
    const { actionHandler, record } = props;
    if (actionEvent.code === 'canApprove') {
      mtsEmitter.emit('showApprovalFlow', { actionHandler, record });
      return;
    }

    actionEvent.openConfirm
      ? useConfirm({
          ...actionEvent.openConfirm,
          onOk: actionHandler?.bind(null, actionEvent, record),
        })
      : actionHandler?.(actionEvent, record);
  };
</script>
